import * as React from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Modal, Button, Stack, Typography, Divider,
  CircularProgress, Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { getUnoModulo, getUnoInversor, getUnoBateria, getUnoInversorBomb } from './api';
import { cores } from '../styles/styleUtils';

// ── Estilos ────────────────────────────────────────────────────────────
const styleModal = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70vw', maxHeight: '90vh',
  bgcolor: 'transparent', borderRadius: 2,
  border: `1px solid ${cores.chumbo}`, borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24, p: 3, overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
};

const styleSubModal = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50vw', maxHeight: '85vh',
  bgcolor: 'transparent', borderRadius: 2,
  border: `1px solid ${cores.chumbo}`, borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24, p: 3, overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
};

const campo = (label, value) => (
  <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
    <Typography variant="body2" sx={{ minWidth: 140, color: 'rgba(255,255,255,0.7)' }}>{label}:</Typography>
    <Chip label={value || '—'} size="small" sx={{ fontWeight: 'bold', backgroundColor: '#fff' }} />
  </Stack>
);

// ── Sub-modal Ver Infos ────────────────────────────────────────────────
function ModalVerInfos({ open, onClose, produto, titulo }) {
  if (!produto) return null;

  const baixarPdf = (arquivo, nome) => {
    if (!arquivo) {
      window.alert('Documento não disponível para download.');
      return;
    }

    try {
      // Compatibilidade com os dois formatos usados no cadastro:
      // 1) base64 puro
      // 2) data URI completa: data:application/pdf;base64,...
      let base64 = arquivo;
      let mimeType = 'application/pdf';

      if (typeof arquivo !== 'string') {
        window.alert('Formato de documento inválido.');
        return;
      }

      if (arquivo.startsWith('data:')) {
        const partes = arquivo.split(',');
        if (partes.length < 2) {
          window.alert('Documento armazenado em formato inválido.');
          return;
        }

        const cabecalho = partes[0];
        base64 = partes.slice(1).join(',');

        const mimeEncontrado = cabecalho.match(/^data:([^;]+);base64$/i);
        if (mimeEncontrado?.[1]) {
          mimeType = mimeEncontrado[1];
        }
      }

      // Remove espaços/quebras de linha eventualmente persistidos no base64.
      base64 = base64.replace(/\s/g, '');

      // Blob é mais confiável que data URL para PDFs maiores (datasheets/manuais).
      const binario = window.atob(base64);
      const bytes = new Uint8Array(binario.length);

      for (let i = 0; i < binario.length; i += 1) {
        bytes[i] = binario.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: mimeType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${nome}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Libera a URL temporária após o navegador iniciar o download.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Erro ao baixar documento do equipamento:', error);
      window.alert('Não foi possível baixar o documento. Verifique o arquivo cadastrado.');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={styleSubModal}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: cores.textoClaro }}>{titulo}</Typography>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon />} color="error" variant="outlined">
            Fechar
          </Button>
        </Stack>

        {/* Foto */}
        {produto.foto && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <img
              src={produto.foto.startsWith('data:') ? produto.foto : `data:image/jpeg;base64,${produto.foto}`}
              alt={titulo}
              style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }}
            />
          </Box>
        )}

        <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Especificações */}
        <Typography variant="subtitle2" sx={{ color: cores.laranja }} mb={1}>Especificações Técnicas</Typography>
        {produto.modelo       && campo('Modelo',       produto.modelo)}
        {produto.fabricante   && campo('Fabricante',   produto.fabricante)}
        {produto.marca        && campo('Marca',        produto.marca)}
        {produto.potencia     && campo('Potência',     `${produto.potencia} ${produto.tensaonominal ? 'kW' : 'Wp'}`)}
        {produto.tensaonominal && campo('Tensão Nominal', `${produto.tensaonominal} V`)}
        {produto.correntenominal && campo('Corrente Nominal', `${produto.correntenominal} A`)}
        {produto.eficiencia   && campo('Eficiência',  `${produto.eficiencia}%`)}
        {produto.garantia     && campo('Garantia',    `${produto.garantia} anos`)}
        {produto.garantiafabrica && campo('Garantia Fábrica', `${produto.garantiafabrica} anos`)}
        {produto.garantiaeficiencia && campo('Garantia Eficiência', `${produto.garantiaeficiencia} anos`)}
        {produto.autonomia    && campo('Autonomia',   `${produto.autonomia} kWh`)}
        {produto.vidautil     && campo('Vida Útil',   `${produto.vidautil} ciclos`)}
        {produto.peso         && campo('Peso',        `${produto.peso} kg`)}

        <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Downloads */}
        <Typography variant="subtitle2" sx={{ color: cores.laranja }} mb={1}>Documentos</Typography>
        <Stack direction="row" gap={2} flexWrap="wrap">
          {produto.datasheet ? (
            <Button
              variant="contained" size="small" startIcon={<DownloadIcon />}
              onClick={() => baixarPdf(produto.datasheet, `Datasheet_${produto.modelo || 'produto'}`)}
              sx={{ backgroundColor: cores.laranja, '&:hover': { backgroundColor: cores.laranjaEscuro } }}
            >
              Datasheet
            </Button>
          ) : (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>Datasheet não cadastrado</Typography>
          )}
          {produto.registroinmetro && (
            <Button
              variant="outlined" size="small" startIcon={<DownloadIcon />}
              onClick={() => baixarPdf(produto.registroinmetro, `INMETRO_${produto.modelo || 'produto'}`)}
              sx={{ color: cores.textoClaro, borderColor: cores.laranja, '&:hover': { borderColor: cores.laranjaEscuro, backgroundColor: 'rgba(209,102,9,0.15)' } }}
            >
              Registro INMETRO
            </Button>
          )}
        </Stack>
      </Box>
    </Modal>
  );
}

// ── Card de Equipamento ────────────────────────────────────────────────
function CardEquipamento({ titulo, campos, onVerInfos, loading }) {
  return (
    <Box sx={{ background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`, border: `1px solid ${cores.chumbo}`, borderTop: `3px solid ${cores.laranja}`, borderRadius: 2, p: 2, color: 'white', flex: 1, minWidth: 200 }}>
      <Typography variant="subtitle2" fontWeight="bold" mb={1} sx={{ color: 'white' }}>{titulo}</Typography>
      {campos.map(([label, value]) => (
        <Stack key={label} direction="row" alignItems="center" gap={1} mb={0.3}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 100 }}>{label}:</Typography>
          <Chip label={value || '—'} size="small" sx={{ backgroundColor: 'white', fontWeight: 'bold', fontSize: 11 }} />
        </Stack>
      ))}
      <Button
        size="small" variant="contained" startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <VisibilityIcon />}
        onClick={onVerInfos} disabled={loading}
        sx={{ mt: 1, backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#388e3c' } }}
      >
        Ver infos
      </Button>
    </Box>
  );
}

// ── Componente Principal ───────────────────────────────────────────────
export default function ModalVerEquipamentos({ open, onClose, proposta }) {
  const [subModal, setSubModal] = useState({ open: false, produto: null, titulo: '' });
  const [loading, setLoading] = useState({ modulo: false, inversor: false, bateria: false });
  const [moduloMarca, setModuloMarca] = useState('—');
  const [inversorFabricante, setInversorFabricante] = useState('—');

  const kit = proposta?.kit;
  const nomeProduto = proposta?.produto?.nome || '';
  const isBombeamento = nomeProduto.includes('Bombeamento');
  const isOffGrid = nomeProduto.includes('Off');

  // Busca fabricante/marca dos equipamentos ao abrir (apenas off-grid)
  React.useEffect(() => {
    if (!open || !isOffGrid) return;
    const buscarDados = async () => {
      try {
        if (kit?.modulo?._id) {
          const mod = await getUnoModulo({ _id: kit.modulo._id });
          if (mod?.marca) setModuloMarca(mod.marca);
        }
        if (kit?.inversor?._id) {
          const inv = await getUnoInversor({ _id: kit.inversor._id });
          if (inv?.fabricante) setInversorFabricante(inv.fabricante);
        }
      } catch(e) { /* usa padrão */ }
    };
    buscarDados();
  }, [open]);

  if (!proposta) return null;

  const abrirInfos = async (tipo) => {
    setLoading((prev) => ({ ...prev, [tipo]: true }));
    try {
      let produto = null;
      if (tipo === 'modulo')   produto = await getUnoModulo({ _id: kit.modulo._id });
      if (tipo === 'inversor') produto = await getUnoInversor({ _id: kit.inversor._id });
      if (tipo === 'bateria')  produto = await getUnoBateria({ _id: kit.bateria._id });
      if (tipo === 'inversorbombeamento') produto = await getUnoInversorBomb({ _id: kit.inversor._id });
      const titulos = {
        modulo: 'Módulo Fotovoltaico',
        inversor: 'Inversor',
        bateria: 'Bateria',
        inversorbombeamento: 'Inversor de Bombeamento',
      };
      setSubModal({ open: true, produto, titulo: titulos[tipo] });
    } catch (e) {
      window.alert('Erro ao buscar informações do produto');
    }
    setLoading((prev) => ({ ...prev, [tipo]: false }));
  };

  const titulo = isBombeamento
    ? 'Sistema Vendido - Bombeamento Solar'
    : isOffGrid
    ? 'Sistema Vendido - Gerador Off-Grid'
    : 'Sistema Vendido - Gerador On-Grid';

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={styleModal}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: cores.textoClaro }}>{titulo}</Typography>
            <Button size="small" onClick={onClose} startIcon={<CloseIcon />} color="error" variant="outlined">
              Fechar
            </Button>
          </Stack>

          {/* Linha 1: Potências */}
          <Stack direction="row" gap={2} mb={2} flexWrap="wrap">
            {campo('Potência CC', `${kit?.potenciacc} kWp`)}
            {campo(isBombeamento ? 'Potência CA' : 'Potência CA',
              isOffGrid
                ? `${kit?.inversor?.potencia || kit?.potenciainversor || '—'} kW`
                : `${kit?.potenciainversor} ${isBombeamento ? 'CV' : 'kW'}`
            )}
            {campo('Qtde Módulos',    isOffGrid ? kit?.modulo?.qtde   : kit?.quantidademodulos)}
            {campo('Qtde Inversores', isOffGrid ? kit?.inversor?.qtde : kit?.quantidadeinversores)}
            {!isBombeamento && !isOffGrid && campo('Distribuidora', proposta.distribuidora?.nome)}
            {!isBombeamento && !isOffGrid && campo('Disjuntor Padrão',
              kit?.disjuntorminino?.tipodisjuntor?.nome
                ? `${kit.disjuntorminino.tipodisjuntor.nome} ${kit.disjuntorminino.potenciadisjuntor?.valor}A`
                : '—'
            )}
            {isBombeamento && campo('Tensão', `${kit?.inversor?.tensao || '—'} V`)}
            {isOffGrid     && campo('Tensão', `${kit?.inversor?.tensao || '—'} V`)}
          </Stack>

          <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.2)' }} />

          {/* Linha 2: Módulo + Inversor */}
          <Stack direction="row" gap={2} mb={2} flexWrap="wrap">
            <CardEquipamento
              titulo="Módulo FV"
              campos={[
                ['Modelo', kit?.modulo?.modelo],
                ['Fabricante', kit?.modulo?.marca || kit?.modulo?.fabricante || (isOffGrid ? moduloMarca : '—')],
              ]}
              onVerInfos={() => abrirInfos('modulo')}
              loading={loading.modulo}
            />
            <CardEquipamento
              titulo={isBombeamento ? 'Inversor Bombeamento' : 'Inversor'}
              campos={[
                ['Modelo', kit?.inversor?.modelo],
                ['Fabricante', kit?.inversor?.fabricante || kit?.inversor?.marca || (isOffGrid ? inversorFabricante : '—')],
              ]}
              onVerInfos={() => abrirInfos(isBombeamento ? 'inversorbombeamento' : 'inversor')}
              loading={loading.inversor}
            />
          </Stack>

          {/* Bateria (Off-Grid) */}
          {isOffGrid && kit?.bateria && (
            <>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
              <CardEquipamento
                titulo="Bateria"
                campos={[
                  ['Modelo', kit.bateria?.modelo],
                  ['Quantidade', kit.bateria?.quantidade || 1],
                  ['Potência Total', `${kit.bateria?.potencia} kW`],
                  ['Energia Total', `${kit.bateria?.autonomia} kWh`],
                ]}
                onVerInfos={() => abrirInfos('bateria')}
                loading={loading.bateria}
              />
            </>
          )}
        </Box>
      </Modal>

      {/* Sub-modal Ver Infos */}
      <ModalVerInfos
        open={subModal.open}
        onClose={() => setSubModal({ open: false, produto: null, titulo: '' })}
        produto={subModal.produto}
        titulo={subModal.titulo}
      />
    </>
  );
}

ModalVerEquipamentos.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  proposta: PropTypes.object,
};